import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { 
  Calculator, 
  LogOut, 
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  TrendingUp
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMargins = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [margins, setMargins] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMargin, setEditingMargin] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    type: "percentage",
    value: 25,
    min_value: 0,
    max_value: 100,
    description: "",
    active: true
  });

  useEffect(() => {
    loadMargins();
  }, []);

  const loadMargins = async () => {
    try {
      const response = await axios.get(`${API}/margins?active=true`, { headers: getAuthHeaders() });
      setMargins(response.data);
    } catch (error) {
      toast.error("Erro ao carregar margens");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const openNewDialog = () => {
    setEditingMargin(null);
    setFormData({
      name: "",
      type: "percentage",
      value: 25,
      min_value: 0,
      max_value: 100,
      description: "",
      active: true
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (margin) => {
    setEditingMargin(margin);
    setFormData({
      name: margin.name,
      type: margin.type,
      value: margin.value,
      min_value: margin.min_value,
      max_value: margin.max_value,
      description: margin.description,
      active: margin.active
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        value: parseFloat(formData.value) || 0,
        min_value: parseFloat(formData.min_value) || 0,
        max_value: parseFloat(formData.max_value) || 100
      };

      // Validar sobreposição com outras margens (excluindo a que está a ser editada)
      const others = margins.filter(m => editingMargin ? m.id !== editingMargin.id : true);
      const overlapping = others.find(m => {
        const mMin = parseFloat(m.min_value);
        const mMax = parseFloat(m.max_value);
        // Sobreposição: ranges intersectam-se
        return !(payload.max_value <= mMin || payload.min_value >= mMax);
      });
      if (overlapping) {
        const unit = payload.type === "fixed" ? "€" : "%";
        const confirmIt = window.confirm(
          `Atenção: o intervalo [${payload.min_value}${unit} - ${payload.max_value}${unit}] sobrepõe-se com "${overlapping.name}" [${overlapping.min_value}${unit} - ${overlapping.max_value}${unit}]. Deseja guardar mesmo assim?`
        );
        if (!confirmIt) return;
      }
      const identical = others.find(m => 
        parseFloat(m.min_value) === payload.min_value && parseFloat(m.max_value) === payload.max_value
      );
      if (identical) {
        toast.warning(`Intervalo idêntico ao da margem "${identical.name}".`);
      }

      if (editingMargin) {
        await axios.put(`${API}/margins/${editingMargin.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Margem atualizada com sucesso!");
      } else {
        await axios.post(`${API}/margins`, payload, { headers: getAuthHeaders() });
        toast.success("Margem criada com sucesso!");
      }
      setIsDialogOpen(false);
      loadMargins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar margem");
    }
  };

  const handleDelete = async (marginId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar esta margem?")) return;
    try {
      await axios.delete(`${API}/margins/${marginId}`, { headers: getAuthHeaders() });
      toast.success("Margem eliminada");
      loadMargins();
    } catch (error) {
      toast.error("Erro ao eliminar margem");
    }
  };

  const formatValue = (margin) => {
    if (margin.type === "percentage") {
      return `${margin.value}%`;
    }
    return `€${margin.value.toFixed(2)}`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-margins-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-manrope font-bold text-lg text-foreground">Evolutt</span>
              <p className="text-xs text-muted-foreground">Margens</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Back & Title */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="sm" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div>
              <h1 className="font-manrope font-extrabold text-2xl text-foreground">Margens</h1>
              <p className="text-muted-foreground">Configurar margens de outsourcing</p>
            </div>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-brand-600 text-white hover:bg-brand-700 font-bold"
                onClick={openNewDialog}
                data-testid="add-margin-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nova Margem
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold text-xl">
                  {editingMargin ? "Editar Margem" : "Nova Margem"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Margem Padrão Outsourcing"
                    data-testid="name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Descrição</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrição da margem"
                    data-testid="description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                      <SelectTrigger data-testid="type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentagem (%)</SelectItem>
                        <SelectItem value="fixed">Valor Fixo (€)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{formData.type === "fixed" ? "Valor Padrão (€)" : "Valor Padrão (%)"}</Label>
                    <Input
                      type="number"
                      step={formData.type === "fixed" ? "50" : "0.1"}
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                      className="font-mono"
                      data-testid="value-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{formData.type === "fixed" ? "Valor Mínimo (€)" : "Valor Mínimo (%)"}</Label>
                    <Input
                      type="number"
                      step={formData.type === "fixed" ? "50" : "0.1"}
                      value={formData.min_value}
                      onChange={(e) => setFormData({ ...formData, min_value: e.target.value })}
                      className="font-mono"
                      data-testid="min-value-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{formData.type === "fixed" ? "Valor Máximo (€)" : "Valor Máximo (%)"}</Label>
                    <Input
                      type="number"
                      step={formData.type === "fixed" ? "50" : "0.1"}
                      value={formData.max_value}
                      onChange={(e) => setFormData({ ...formData, max_value: e.target.value })}
                      className="font-mono"
                      data-testid="max-value-input"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <Label>Ativo</Label>
                  <Switch
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                    data-testid="active-switch"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-primary text-primary-foreground" data-testid="save-btn">
                    {editingMargin ? "Guardar Alterações" : "Criar Margem"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Margins List */}
        <Card className="bg-white border border-border/60 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : margins.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <TrendingUp className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nenhuma margem configurada</p>
                <p className="text-sm text-muted-foreground">Clique em "Nova Margem" ou carregue os dados iniciais</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Tipo</TableHead>
                    <TableHead className="font-semibold">Valor</TableHead>
                    <TableHead className="font-semibold">Intervalo</TableHead>
                    <TableHead className="font-semibold">Estado</TableHead>
                    <TableHead className="font-semibold text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {margins.map((margin) => (
                    <TableRow key={margin.id} data-testid={`margin-row-${margin.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{margin.name}</p>
                          <p className="text-xs text-muted-foreground">{margin.description}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {margin.type === "percentage" ? "Percentagem" : "Fixo"}
                      </TableCell>
                      <TableCell className="font-mono font-bold text-lg">{formatValue(margin)}</TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {margin.type === "fixed"
                          ? `€${margin.min_value} - €${margin.max_value}`
                          : `${margin.min_value}% - ${margin.max_value}%`}
                      </TableCell>
                      <TableCell>
                        <Badge variant={margin.active ? "default" : "secondary"}>
                          {margin.active ? "Ativo" : "Inativo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditDialog(margin)}
                            data-testid={`edit-btn-${margin.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDelete(margin.id)}
                            data-testid={`delete-btn-${margin.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminMargins;
